import React from 'react'
import { Badge, Box, SimpleGrid, Stack, Text } from '@chakra-ui/react'
import { format as formatDate } from 'date-fns'
import type {
  LeadGroupUser,
  RentalApplication,
  RentalHistoryAddressSnapshot
} from '@homevest/types/rental-applications'
import { regions } from '@homevest/utils'

import { ContentSectionCard } from 'components/TailwindUIToolkit'
import { formatPhoneFromDatabase } from 'lib/formatting'

type RentalHistoryPanelProps = {
  rentalApplication: RentalApplication
  leadGroupUsers: LeadGroupUser[]
}

const formatAddress = (address: RentalHistoryAddressSnapshot | string | null | undefined) => {
  if (!address) {
    return 'N/A'
  }

  if (typeof address === 'string') {
    try {
      return formatAddress(JSON.parse(address))
    } catch {
      return address
    }
  }

  if (address.formatted) {
    return address.formatted
  }

  const cityStateZip = [address.city, address.state, address.zip].filter(Boolean).join(' ')
  return [address.line1, address.line2, cityStateZip].filter(Boolean).join(', ') || 'N/A'
}

const Detail = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box>
    <Text fontSize='xs' color='gray.500'>
      {label}
    </Text>
    <Text marginTop={0}>{value || 'N/A'}</Text>
  </Box>
)

const RentalHistoryPanel: React.FC<React.PropsWithChildren<RentalHistoryPanelProps>> = ({
  rentalApplication,
  leadGroupUsers
}) => {
  const rentalHistories = rentalApplication.rental_application_rental_histories || []
  const shouldShow =
    rentalApplication.mls_listing.region === regions.REGIONS.ATLANTA || rentalHistories.length > 0

  if (!shouldShow) {
    return null
  }

  const historiesByUserId = new Map(rentalHistories.map((history) => [history.user_id, history]))

  return (
    <ContentSectionCard collapsable={true} padding title='Rental History' size='fit-content'>
      <Stack spacing={4}>
        {leadGroupUsers.map((leadGroupUser) => {
          const user = leadGroupUser.users
          const history = historiesByUserId.get(user.id)
          const isCurrentHomeRented = history?.is_current_home_rented

          return (
            <Box key={leadGroupUser.id} borderWidth='1px' borderRadius='md' p={4}>
              <Stack spacing={3}>
                <Box display='flex' justifyContent='space-between' gap={4}>
                  <Text fontWeight='semibold'>
                    {user.first_name} {user.last_name}
                  </Text>
                  {history ? (
                    <Badge colorScheme='green'>Submitted</Badge>
                  ) : (
                    <Badge colorScheme='gray'>Not submitted</Badge>
                  )}
                </Box>
                <SimpleGrid columns={[1, 2, 3]} spacing={4}>
                  <Detail label='Current Address' value={formatAddress(history?.current_address)} />
                  <Detail
                    label='Current home rented'
                    value={history ? (isCurrentHomeRented ? 'Yes' : 'No') : 'N/A'}
                  />
                  <Detail
                    label='Landlord'
                    value={isCurrentHomeRented ? history?.landlord_name_or_company : 'N/A'}
                  />
                  <Detail
                    label='Landlord Phone'
                    value={
                      isCurrentHomeRented
                        ? formatPhoneFromDatabase(history?.landlord_phone) ||
                          history?.landlord_phone
                        : 'N/A'
                    }
                  />
                  <Detail
                    label='Landlord Email'
                    value={isCurrentHomeRented ? history?.landlord_email : 'N/A'}
                  />
                  <Detail
                    label='Release Authorization'
                    value={
                      history?.landlord_release_authorized_at
                        ? formatDate(
                            new Date(history.landlord_release_authorized_at),
                            'M/d/yy h:mm a'
                          )
                        : 'N/A'
                    }
                  />
                </SimpleGrid>
              </Stack>
            </Box>
          )
        })}
      </Stack>
    </ContentSectionCard>
  )
}

export default RentalHistoryPanel
